import { supabase } from '../services/supabase';
import { mirrorOperationalState } from '../services/operationalData';
import { enqueueMutation } from '../store/syncEngine';
import * as Sentry from '@sentry/react';

const PREFIX = 'gmc_';

// localStorage fallback (supabase yoksa veya offline)
function lsLoad<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(PREFIX + key);
    if (raw == null) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

function lsSave<T>(key: string, value: T): void {
  try {
    localStorage.setItem(PREFIX + key, JSON.stringify(value));
  } catch (e) {
    if (e instanceof DOMException && e.name === 'QuotaExceededError') {
      // Çöp kutusunu temizleyerek alan aç, sonra tekrar dene
      try {
        localStorage.removeItem(PREFIX + PERSIST_KEYS.TRASHED_BAGS);
        localStorage.setItem(PREFIX + key, JSON.stringify(value));
        console.warn('[GMC] localStorage doluydu, çöp kutusu temizlendi');
      } catch {
        // Yine de başarısız — kullanıcıya kalıcı uyarı göster
        console.error('[GMC] localStorage kritik dolu, veri kaydedilemedi:', key);
        dispatchEvent(new CustomEvent('gmc:storage-full'));
      }
    }
  }
}

// Senkron okuma (localStorage cache) — mevcut kod uyumu için
export function loadStored<T>(key: string, fallback: T): T {
  return lsLoad(key, fallback);
}

// Operational mirror sadece grow verisi için yapılır — ayarlar ve loglar için değil
const OPERATIONAL_MIRROR_KEYS = new Set([
  'grow_bags',
  'master_mixes',
  'spawn_lots',
  'orders',
]);

// Grow verisi için cloud'dan gelen değeri localStorage ile merge eder.
// Kural: localStorage'daki veri daha yeniyse korunur (updated_at karşılaştırması).
// Bu sayede offline düzenleme startup sync'te ezilmez.
const GROW_MERGE_KEYS = new Set([
  'grow_bags',
  'master_mixes',
  'spawn_lots',
  'orders',
  'tasks',
  'loss_records',
  'photo_logs',
]);

// Her yazma işleminde timestamp sakla — multi-device sync için
function lsSaveTs(key: string): void {
  try {
    localStorage.setItem(PREFIX + key + '_ts', new Date().toISOString());
  } catch {
    // timestamp kaydedilemezse sessizce devam et
  }
}

function lsLoadTs(key: string): string | null {
  return localStorage.getItem(PREFIX + key + '_ts');
}

// Senkron yazma (localStorage) + async Supabase
// Supabase hatası: offline queue'ya alınır, Sentry'e raporlanır, UI'ya bildirilir
export function saveStored<T>(key: string, value: T): boolean {
  lsSave(key, value);
  lsSaveTs(key);
  if (supabase) {
    supabase.from('gmc_store').upsert(
      { key, value, updated_at: new Date().toISOString() },
      { onConflict: 'key' }
    ).then(({ error }) => {
      if (error) {
        console.error('[GMC] Supabase yazma hatası:', key, error.message);
        Sentry.captureException(new Error(`gmc_store upsert failed: ${key}`), {
          extra: { key, supabaseError: error.message },
        });
        // Grow verisi için offline queue'ya al — online olunca retry yapılır
        if (GROW_MERGE_KEYS.has(key)) {
          enqueueMutation({ type: 'saveGrowStore', payload: { key, value } });
        }
        // UI'ya bildir — toast App.tsx'te dinliyor
        dispatchEvent(new CustomEvent('gmc:sync-error', { detail: { key } }));
      }
    });
    if (OPERATIONAL_MIRROR_KEYS.has(key)) {
      mirrorOperationalState(key, value).catch((error) => {
        console.error('[GMC] Operasyon tablosu yazma hatası:', key, error.message);
      });
    }
  }
  return true;
}

export function clearStored(key: string): void {
  localStorage.removeItem(PREFIX + key);
  if (supabase) {
    supabase.from('gmc_store').delete().eq('key', key);
  }
}

// Uygulama başlarken Supabase'den veriyi çekip localStorage ile conflict-aware merge yapar.
// Grow verisi (bag, order vs.) için localStorage kazanır — offline düzenleme korunur.
// Ayarlar ve config için cloud kazanır — son kaydedilen ayar kullanılır.
export async function syncFromSupabase(): Promise<void> {
  if (!supabase) return;
  const { data, error } = await supabase.from('gmc_store').select('key, value, updated_at');
  if (error || !data) return;

  for (const row of data) {
    const lsKey = PREFIX + row.key;

    if (GROW_MERGE_KEYS.has(row.key)) {
      // Grow verisi: updated_at karşılaştırmasıyla cloud vs local — hangisi daha yeniyse kazanır.
      // Bu sayede multi-device senkronizasyon doğru çalışır: farklı cihazlardan yapılan
      // güncellemeler startup'ta birbirini ezmez.
      const existing = localStorage.getItem(lsKey);
      if (!existing) {
        localStorage.setItem(lsKey, JSON.stringify(row.value));
        lsSaveTs(row.key); // cloud'dan gelen veriyi local ts ile de işaretle
      } else {
        const localTs = lsLoadTs(row.key);
        const cloudTs = row.updated_at ?? '';
        if (cloudTs > (localTs ?? '')) {
          // Cloud daha güncel — local'i güncelle
          localStorage.setItem(lsKey, JSON.stringify(row.value));
          lsSaveTs(row.key);
          console.info('[GMC] Sync: cloud verisi daha yeni, local güncellendi:', row.key);
        }
        // Local daha güncel veya eşit — dokunma, offline düzenleme korunur
      }
    } else {
      // Ayarlar ve diğerleri: cloud'u yaz (en güncel ayar kullanılır)
      localStorage.setItem(lsKey, JSON.stringify(row.value));
    }
  }
}

export const PERSIST_KEYS = {
  GROW_BAGS:              'grow_bags',
  MASTER_MIXES:           'master_mixes',
  SPAWN_LOTS:             'spawn_lots',
  LOSS_RECORDS:           'loss_records',
  PHOTO_LOGS:             'photo_logs',
  TRASHED_BAGS:           'trashed_bags',
  ORDERS:                 'orders',
  TASKS:                  'tasks',
  SETTINGS_LOG:           'settings_log',
  CLIMATE_SETTINGS:       'climate_settings',
  BE_SETTINGS:            'be_settings',
  ALERT_EMAIL_SETTINGS:   'alert_email_settings',
  ALERT_NOTIF_SETTINGS:   'alert_notif_settings',
  DEVICES:                'devices',
  CUSTOM_SPECIES:         'custom_species',
} as const;
